package io.github.erkko68.filament.compose.scene

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.produceState
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import io.github.erkko68.filament.Engine
import io.github.erkko68.filament.compose.LocalFilamentEngine
import io.github.erkko68.filament.compose.noFilamentEngine
import io.github.erkko68.filament.compose.internal.rememberOwned
import io.github.erkko68.filament.gltfio.AssetLoader
import io.github.erkko68.filament.gltfio.FilamentAsset
import io.github.erkko68.filament.gltfio.ResourceConfiguration
import io.github.erkko68.filament.gltfio.ResourceLoader
import io.github.erkko68.filament.gltfio.TextureProvider
import io.github.erkko68.filament.gltfio.createKtx2Provider
import io.github.erkko68.filament.gltfio.createStbProvider
import kotlin.coroutines.cancellation.CancellationException
import kotlin.math.sqrt

/**
 * A loaded glTF asset whose GPU resources are ready. Obtain via [rememberGltfAsset].
 *
 * Pass this to one or more [GltfInstance] composables to place copies of the model in the
 * scene without re-importing the asset each time.
 *
 * ### Instance lifetime
 * `gltfio` has no API to destroy a single instance — every [GltfInstance] created from this
 * asset stays alive until the asset itself is destroyed (i.e. until the [rememberGltfAsset]
 * call that produced it leaves composition). Reusing one asset across many instances is cheap,
 * but avoid churning hundreds of short-lived instances from a single long-lived asset.
 */
// @Stable: `isReady` is snapshot-backed and the handles are stable; `primaryInstanceClaimed` is
// internal bookkeeping read only from an effect.
@Stable
class GltfAsset internal constructor(
    internal val filamentAsset: FilamentAsset,
    internal val assetLoader: AssetLoader,
) {
    /**
     * True when all GPU resources (textures, buffers) have been uploaded.
     * [GltfInstance] uses this to avoid rendering incomplete models.
     */
    var isReady by mutableStateOf(false)
        internal set

    /**
     * Fraction of the GPU resources uploaded so far, 0 to 1; reaches 1 with [isReady]. Covers the upload only:
     * reading the bytes happens in your `load` lambda, before the asset exists.
     */
    var progress by mutableFloatStateOf(0f)
        internal set

    /** The names of the asset's `KHR_materials_variants`, for [GltfInstance]'s `materialVariant`; empty without any. */
    val materialVariants: List<String> by lazy {
        val primary = filamentAsset.instance
        List(primary.materialVariantCount) { primary.getMaterialVariantName(it).orEmpty() }
    }

    /**
     * The centre of the model's bounding box, in the model's own units, for [CameraState.fitting]. An instance's
     * `position`, `scale` and `fitToSize` move the model away from it.
     */
    val center: Position get() = bounds.first

    /** The radius around [center] that holds the whole model, in its own units; 0 for a file that declares no bounds. */
    val radius: Float get() = bounds.second

    private val bounds: Pair<Position, Float> by lazy {
        val box = filamentAsset.boundingBox
        val (x, y, z) = box.extent()
        // gltfio leaves the box inverted (min > max) when no primitive has bounds.
        if (minOf(x, y, z) < 0f || !(x + y + z).isFinite()) Position(0f) to 0f
        else box.center().let { Position(it[0], it[1], it[2]) } to sqrt(x * x + y * y + z * z)
    }

    /**
     * Whether the asset's built-in primary instance is currently held by a [GltfInstance].
     * `createInstance` can fail on some platforms; a failing instance may fall back to the
     * primary, but only one at a time — silently aliasing one instance under two composables
     * would make them fight over the same transform.
     */
    internal var primaryInstanceClaimed = false

    /** The resources couldn't be loaded: [rememberGltfAsset] returns null from then on. */
    internal var failed by mutableStateOf(false)

    /** The loader uploading this asset's resources, until the load completes or is abandoned. */
    internal var resourceLoader: ResourceLoader? = null

    /** Texture providers [resourceLoader] decodes with; destroyed after it. */
    internal var textureProviders: List<TextureProvider> = emptyList()

    /** Cancels an unfinished load and destroys its loader; a no-op once released. */
    internal fun releaseResourceLoader() {
        val loader = resourceLoader ?: return
        resourceLoader = null
        if (!isReady) loader.asyncCancelLoad()
        loader.destroy()
        textureProviders.forEach { it.destroy() }
        textureProviders = emptyList()
    }
}

/**
 * Sync overload used internally by the suspend-lambda version once the bytes are ready.
 * Kept internal because in Compose Multiplatform every realistic byte source ([Res.readBytes],
 * disk, network) is suspending — public callers should use the suspend-lambda overload.
 *
 * Returns null (rather than throwing inside composition) when the bytes don't parse as
 * glb/glTF, invoking [onError] once with the failure.
 *
 * @param bytes Raw glb/glTF binary data. Reloaded only when the *reference* changes.
 */
@Composable
internal fun rememberGltfAsset(
    engine: Engine,
    bytes: ByteArray,
    onError: ((Throwable) -> Unit)? = null,
): GltfAsset? {
    val gltfioContext = rememberGltfioContext(engine)
    val assetLoader = gltfioContext.assetLoader

    // The loader is destroyed with the asset, not in the loading coroutine's `finally`, which only
    // runs after a composition-owned engine may already be gone.
    val gltfAsset = rememberOwned(engine, bytes, gltfioContext, dependsOn = listOf(gltfioContext), create = {
        assetLoader.createAsset(bytes)?.let { GltfAsset(it, assetLoader) }
    }) {
        it.releaseResourceLoader()
        assetLoader.destroyAsset(it.filamentAsset)
    }

    if (gltfAsset == null) {
        // Bytes were produced but failed to parse — report once, keyed on the bytes.
        LaunchedEffect(bytes, assetLoader) {
            onError?.invoke(
                IllegalArgumentException("Failed to parse glTF/glb — the bytes are not valid glb/glTF data"),
            )
        }
        return null
    }

    LaunchedEffect(gltfAsset) {
        val resourceLoader = ResourceLoader(ResourceConfiguration(engine, normalizeSkinningWeights = true))
        gltfAsset.resourceLoader = resourceLoader
        gltfAsset.textureProviders = listOf(createStbProvider(engine), createKtx2Provider(engine)).also { (stb, ktx2) ->
            resourceLoader.addTextureProvider("image/png", stb)
            resourceLoader.addTextureProvider("image/jpeg", stb)
            resourceLoader.addTextureProvider("image/ktx2", ktx2)
        }
        // Only self-contained bytes can be loaded: nothing here resolves a .gltf's .bin or image files. Checked up
        // front because asyncBeginLoad reports a missing file on desktop only; elsewhere it loads what it has.
        // (data: URIs are listed too, but gltfio decodes those itself.)
        val external = gltfAsset.filamentAsset.resourceUris.filterNot { it.startsWith("data:") }
        if (external.isNotEmpty() || !resourceLoader.asyncBeginLoad(gltfAsset.filamentAsset)) {
            gltfAsset.releaseResourceLoader()
            gltfAsset.failed = true
            onError?.invoke(
                IllegalArgumentException(
                    "Failed to load the glTF's resources" +
                        if (external.isEmpty()) "" else " — it references external files: ${external.joinToString()}",
                ),
            )
            return@LaunchedEffect
        }
        while (resourceLoader.asyncGetLoadProgress() < 1.0f) {
            resourceLoader.asyncUpdateLoad()
            gltfAsset.progress = resourceLoader.asyncGetLoadProgress()
            withFrameNanos { }
        }
        gltfAsset.progress = 1f
        gltfAsset.isReady = true
        gltfAsset.releaseResourceLoader()
    }

    return gltfAsset.takeUnless { it.failed }
}

/**
 * Asynchronously loads a glTF/glb asset and keeps it alive as long as the calling composable
 * is in the composition.
 *
 * Returns null while the bytes load **and** on failure; the asset it returns then keeps uploading
 * to the GPU until [GltfAsset.isReady]. It never throws inside composition, so a
 * bad model can't crash the app. Pass [onError] to react to failures (show a placeholder,
 * log, retry). Every failure mode is reported: the [load] lambda throwing (missing file,
 * network error), the bytes failing to parse as glb/glTF, and a resource they reference being
 * missing (only self-contained bytes load: a .glb, or a .gltf with embedded data).
 *
 * Can be called either inside `rememberFilamentScene { }` (engine is picked up from
 * [LocalFilamentEngine]) or outside it by hoisting the engine via [rememberFilamentEngine]:
 *
 * ```kotlin
 * // Inside rememberFilamentScene — engine implicit
 * GltfInstance(asset = rememberGltfAsset { Res.readBytes("Duck.glb") }, ...)
 *
 * // Outside a scene — engine hoisted, asset can outlive any single scene
 * val engine = rememberFilamentEngine()
 * val duck   = rememberGltfAsset(engine = engine) { Res.readBytes("Duck.glb") }
 * val scene  = rememberFilamentScene(engine = engine) { GltfInstance(asset = duck, ...) }
 *
 * // Telling loading from failure: a captured error, and isReady (the GPU upload runs after parsing)
 * var error by remember { mutableStateOf<Throwable?>(null) }
 * val model = rememberGltfAsset(engine = engine, onError = { error = it }) { loadFromNetwork() }
 * FilamentSceneView(engine = engine) { GltfInstance(asset = model) }
 * when {
 *     error != null           -> ErrorPlaceholder()   // failed
 *     model?.isReady != true  -> LoadingIndicator(model?.progress)   // reading, parsing or uploading
 * }
 * ```
 *
 * Each [GltfInstance] created from the returned asset lives until the asset is destroyed
 * (when this call leaves composition) — see [GltfAsset].
 *
 * @param key Reloads the asset when this value changes. Defaults to [Unit] for static assets.
 * @param onError Invoked once if [load] throws, the bytes don't parse, or their resources can't be loaded.
 *   The asset stays null.
 * @param engine The Filament engine that owns the asset's GPU resources. Defaults to the
 *   engine in the current composition scope.
 * @param load Suspend function that produces the raw glb/glTF bytes.
 */
@Composable
fun rememberGltfAsset(
    key: Any = Unit,
    onError: ((Throwable) -> Unit)? = null,
    engine: Engine = LocalFilamentEngine.current ?: noFilamentEngine(),
    load: suspend () -> ByteArray,
): GltfAsset? {
    val bytes by produceState<ByteArray?>(initialValue = null, key) {
        value = try {
            load()
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            onError?.invoke(e)
            null
        }
    }
    return bytes?.let { rememberGltfAsset(engine, it, onError) }
}
